import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Receipt, Sparkle, CalendarCheck, ChartLineUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { centsToUsd, listBillingFamilies } from "@/lib/billing";
import AutopayStatusChip from "@/components/billing/AutopayStatusChip";
import AutopayActivityPanel from "@/components/billing/AutopayActivityPanel";
import AdminAutopayRunner from "@/components/billing/AdminAutopayRunner";
import NotificationLogPanel from "@/components/notifications/NotificationLogPanel";
import BulkTuitionPanel from "@/components/billing/BulkTuitionPanel";
import BillingConfigCard from "@/components/billing/BillingConfigCard";

export default function AdminBilling() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showBulk, setShowBulk] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await listBillingFamilies();
      setRows(list);
    } catch (err) {
      toast.error(err.message || "Could not load billing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (r.family?.display_name || "").toLowerCase().includes(q) ||
      (r.billing_email || "").toLowerCase().includes(q)
    );
  });

  const kpis = {
    families: rows.length,
    unpaidInvoiceValue: rows.reduce((a, r) => a + (r.totals?.open || 0), 0),
    markedPaidInvoiceValue: rows.reduce((a, r) => a + (r.totals?.paid || 0), 0),
  };

  return (
    <div data-testid="admin-billing" className="space-y-6">
      <section>
        <p className="overline text-primary-hover">Operational</p>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
              Billing
            </h1>
            <p className="mt-2 max-w-2xl text-neutral-600">
              Manage billing profiles and invoices per family. Invoices stay as
              drafts until you issue them to the parent.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/admin/billing/ledger"
              data-testid="billing-ledger-link"
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-neutral-50"
            >
              <ChartLineUp size={14} weight="duotone" />
              Receivables &amp; receipts
            </Link>
            <Link
              to="/admin/billing/plans"
              data-testid="plans-overview-link"
              className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white px-4 py-2.5 text-sm font-semibold text-primary-hover hover:bg-primary/5"
            >
              <CalendarCheck size={14} weight="duotone" />
              Tuition plans
            </Link>
            <Link
              to="/admin/billing/pricing"
              data-testid="product-pricing-link"
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-neutral-50"
            >
              Pricing &amp; plan
            </Link>
            <button
              type="button"
              data-testid="bulk-tuition-open"
              onClick={() => setShowBulk(true)}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98]"
            >
              <Sparkle size={14} weight="duotone" />
              Generate monthly tuition
            </button>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Kpi testId="billing-kpi-families" label="Active families" value={String(kpis.families)} />
        <Kpi
          testId="billing-kpi-open"
          label="Unpaid invoice value"
          value={centsToUsd(kpis.unpaidInvoiceValue)}
          detail="Not reconciled for receipts or agency responsibility"
          accent
        />
        <Kpi
          testId="billing-kpi-paid"
          label="Marked-paid invoice value"
          value={centsToUsd(kpis.markedPaidInvoiceValue)}
          detail="Invoice status, not a cash-receipts total"
        />
      </section>

      <BillingConfigCard />

      <section className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-neutral-900">Need a reconciled view?</p>
            <p className="mt-1 max-w-2xl text-sm text-neutral-600">
              Use Receivables &amp; receipts for actual family and agency receipts,
              aging, and claim variance. It stays separate from invoice management
              so routine billing work does not imply that raw invoice totals are cash.
            </p>
          </div>
          <Link
            to="/admin/billing/ledger"
            data-testid="billing-receivables-inline-link"
            className="inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover"
          >
            <ChartLineUp size={14} weight="duotone" />
            Open report
          </Link>
        </div>
      </section>

      <section className="rounded-2xl border border-neutral-200 bg-white p-5">
        <div className="mb-4 flex items-center gap-2">
          <MagnifyingGlass size={18} weight="duotone" className="text-primary-hover" />
          <p className="overline text-neutral-500">Search families</p>
        </div>
        <input
          data-testid="billing-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="e.g. Cohen, parent@daycare.test"
          className="w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center">
          <p className="font-heading text-base font-semibold text-neutral-900">
            No billing profiles yet
          </p>
          <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-500">
            Profiles are auto-created when an enrollment is converted to an
            active family.
          </p>
        </div>
      ) : (
        <div
          data-testid="billing-table"
          className="overflow-hidden rounded-2xl border border-neutral-200 bg-white"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 text-left font-mono text-[11px] font-medium uppercase tracking-[0.1em] text-neutral-500">
                <th className="px-4 py-3">Family</th>
                <th className="px-4 py-3">Billing email</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Autopay</th>
                <th className="px-4 py-3 text-right">Unpaid invoices</th>
                <th className="px-4 py-3 text-right">Marked paid</th>
                <th className="px-4 py-3 text-right">Invoices</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`billing-row-${r.id}`}
                  className="border-t border-neutral-100"
                >
                  <td className="px-4 py-3 font-semibold text-neutral-900">
                    {r.family?.display_name || "—"}
                  </td>
                  <td className="px-4 py-3 text-neutral-700">
                    {r.billing_email || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <StatusChip status={r.status} />
                  </td>
                  <td className="px-4 py-3">
                    <AutopayStatusChip
                      status={r.autopay_status || (r.autopay ? "active" : "none")}
                      testId={`billing-row-${r.id}-autopay`}
                    />
                  </td>
                  <td className="tnum px-4 py-3 text-right font-mono">
                    {centsToUsd(r.totals?.open || 0)}
                  </td>
                  <td className="tnum px-4 py-3 text-right font-mono">
                    {centsToUsd(r.totals?.paid || 0)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {r.totals?.count || 0}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/admin/billing/${r.family_id}`}
                      data-testid={`billing-open-${r.family_id}`}
                      className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/10"
                    >
                      <Receipt size={12} weight="duotone" />
                      Manage
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showBulk && (
        <BulkTuitionPanel
          onClose={() => setShowBulk(false)}
          onCompleted={() => load()}
        />
      )}

      <AdminAutopayRunner onCompleted={load} />

      <AutopayActivityPanel />

      <NotificationLogPanel />
    </div>
  );
}

function StatusChip({ status }) {
  const cls =
    status === "active"
      ? "bg-primary/10 text-primary-hover border-primary/30"
      : status === "paused"
        ? "bg-secondary/30 text-neutral-900 border-secondary/50"
        : "bg-neutral-100 text-neutral-500 border-neutral-200";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}
    >
      {status}
    </span>
  );
}

function Kpi({ label, value, detail, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border bg-white p-5 shadow-card ${accent ? "border-primary/30" : "border-neutral-200"}`}
    >
      <p className="overline text-neutral-500">{label}</p>
      <p
        className={`tnum mt-1.5 font-heading text-2xl font-bold tracking-tight ${accent ? "text-primary-hover" : "text-neutral-900"}`}
      >
        {value}
      </p>
      {detail && <p className="mt-1 text-[11px] leading-4 text-neutral-500">{detail}</p>}
    </div>
  );
}
